/**
 * Модификаторы характеристик (`StatModifier` из contracts): выбор по цели, свёртка уровней, применение.
 *
 * Договорённость E5 (её же стоит держать E1/E3/E4):
 * - порядок — как в contracts: сначала сумма всех `add`, затем произведение всех `(1000 + mulPermille) / 1000`,
 *   округление вниз один раз в конце (точная целая арифметика на BigInt);
 * - уровни одного источника складываются: n уровней Tech «+10 %» — один модификатор `+100·n ‰`
 *   ({@link foldLevels}), а не `1,1^n`; разные источники (звезда навыка, Tech, усиление) перемножаются;
 * - множитель не уходит ниже нуля: `mulPermille ≤ −1000` даёт ×0.
 */
import { PERMILLE, type HeroId, type StatId, type StatModifier } from "@newfungame/contracts";

/** Кого спрашиваем: герой, штаб (и пушка) или глобальная характеристика (`coinGain`, `zombieSpeed`). */
export type ModifierScope = HeroId | "hq" | "global";

/**
 * Модификаторы характеристики `stat`, действующие на `scope`:
 * - герой — без `target`, `allHeroes` или этот герой;
 * - `hq` — только `target: "hq"`;
 * - `global` — без `target` (и `allHeroes` для совместимости).
 */
export function selectModifiers(
  modifiers: readonly StatModifier[],
  stat: StatId,
  scope: ModifierScope,
): StatModifier[] {
  return modifiers.filter((m) => {
    if (m.stat !== stat) return false;
    if (scope === "hq") return m.target === "hq";
    if (m.target === undefined || m.target === "allHeroes") return true;
    return m.target === scope;
  });
}

/** n уровней одного источника → те же модификаторы со значением × n. */
export function foldLevels(modifiers: readonly StatModifier[], levels: number): StatModifier[] {
  if (levels <= 0) return [];
  return modifiers.map((m) => ({ ...m, value: m.value * levels }));
}

/** Применяет уже отобранные модификаторы к базовому значению: `add`, затем произведение `mulPermille`, вниз. */
export function applyModifiers(base: number, modifiers: readonly StatModifier[]): number {
  let sum = BigInt(base);
  let num = 1n;
  let den = 1n;
  for (const m of modifiers) {
    if (m.op === "add") {
      sum += BigInt(m.value);
    } else {
      num *= BigInt(Math.max(0, PERMILLE + m.value));
      den *= BigInt(PERMILLE);
    }
  }
  if (sum < 0n) sum = 0n;
  return Number((sum * num) / den);
}

/** Итоговый множитель `mulPermille`-модификаторов в промилле (1000 = ×1), вниз; `add` игнорируются. */
export function multiplierPermille(modifiers: readonly StatModifier[]): number {
  return applyModifiers(
    PERMILLE,
    modifiers.filter((m) => m.op === "mulPermille"),
  );
}
